import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import buildTimeOverviewTableModel from "./timeOverviewTableModel";

/**
 * Replace <a ...>label</a> with just the label text.
 *
 * @param {string} html
 * @returns {string}
 */
function removeAnchorTags(html) {
  if (!html) return "";
  return html.replace(/<a\b[^>]*>(.*?)<\/a>/gi, "$1");
}

// PDF layout constants
const PDF_FONT = { title: 20, subtitle: 16, label: 10, normal: 9, small: 8 };
const PDF_COLORS = {
  rcp26: [212, 234, 255],
  rcp85: [255, 217, 217],
  headerBg: [230, 230, 230],
  borderBlue: [0, 109, 151],
  magenta: [229, 0, 66],
};
// Layout measurements used when sizing table columns. Units: mm
const LAYOUT = {
  leftColMinWidth: 12,
  otherColMinWidth: 8,
  cellPadding: 4,
};

// Layout geometry constants
const MARGINS = { left: 15, right: 15 };
const HEADER = {
  textSize: 12,
  textY: 12,
  imgY: 4,
  imgW: 65,
  barHeight: 2, // height of bar under header
  titleYOffset: 15,
  subtitleYOffset: 15,
};
const BOX = {
  padding: 4,
  topPadding: 8,
  rowGap: 4,
  minHeight: 48,
  infoTitleSize: 11,
  infoTitleGap: 6,
  lineHeight: 4,
  topGap: 20,
  labelFontSize: 10,
  valueFontSize: 9,
};
const TABLE_CHART_LAYOUT = {
  topGap: 20,
  leftColWidth: 70,
  columnGap: 10,
  chartMaxH: 90,
  chartYOffset: -5, // vertical offset (mm) to align chart top with table top
};

// Offset in mm for vertical y-axis label relative to chart image (positive = right, negative = left)
const Y_AXIS_LABEL_OFFSET = 1;
const Y_AXIS_LABEL_FONT_SIZE = 9;

const FOOTER = { barHeight: 2, height: 6, pageNumYOffset: 2.5, fontSize: 9 };

/**
 * Generate a Faktenblatt PDF and return the jsPDF instance.
 *
 * @param {Object} [opts]
 * @param {string} [opts.label]
 * @param {string} [opts.entityDisplayText]
 * @param {string} [opts.selectedEntity]
 * @param {string} [opts.infoText]
 * @param {Object} [opts.nls]
 * @param {Array} [opts.mainTableResult]
 * @param {Array} [opts.aesTableResult]
 * @param {Object} [opts.timeOverviewNls]
 * @param {string|null} [opts.chartImageUrl]
 * @param {Object} [opts.chartDimensions]
 * @param {string|null} [opts.aesChartImageUrl]
 * @param {Object} [opts.aesChartDimensions]
 * @param {string|null} [opts.pdfHeader]
 * @param {string} [opts.Zeitabschnitt]
 * @param {Object|null} [opts.currentChoice]
 * @returns {Promise<import('jspdf').jsPDF>} jsPDF document instance
 */
export default async function createFactSheetPdf(opts = {}) {
  const {
    label = "",
    entityDisplayText = "",
    selectedEntity = "",
    infoText = "",
    nls = {},
    mainTableResult = [],
    aesTableResult = [],
    timeOverviewNls = {},
    chartImageUrl = null,
    chartDimensions = {},
    aesChartImageUrl = null,
    aesChartDimensions = {},
    pdfHeader = null,
    Zeitabschnitt = "",
    currentChoice = null,
  } = opts;

  const doc = new jsPDF();

  try {
    const pageWidth = doc.internal.pageSize.getWidth();
    // Header: left = organization text, right = pdfHeader image (same row)
    const headerY = renderHeader(doc, pageWidth, pdfHeader);

    renderTitleAndSubtitle(
      doc,
      MARGINS.left,
      headerY,
      "NIKLIS Faktenblatt",
      label
    );

    // Info box: compute required height from wrapped text so contents never overflow
    const infoBoxX = MARGINS.left;
    const infoBoxY = headerY + BOX.topGap;
    const infoBoxW = pageWidth - (MARGINS.left + MARGINS.right);
    const infoBoxH = renderInfoBox(doc, infoBoxX, infoBoxY, infoBoxW, {
      infoText,
      nls,
      entityDisplayText,
      selectedEntity,
      Zeitabschnitt,
      currentChoice,
      label,
    });

    // Table and chart area (left table + right chart)
    const mainContentTop = infoBoxY + infoBoxH + TABLE_CHART_LAYOUT.topGap;
    renderTableAndChartAt(doc, {
      pageWidth,
      contentTop: mainContentTop,
      tableResult: mainTableResult,
      timeOverviewNls,
      chartImageUrl,
      chartDimensions,
      yAxisLabel: label,
    });

    const hasAesContent =
      (Array.isArray(aesTableResult) && aesTableResult.length > 0) ||
      Boolean(aesChartImageUrl);
    if (hasAesContent) {
      // Render change-mode content on a dedicated page.
      doc.addPage();
      const aesPageWidth = doc.internal.pageSize.getWidth();
      const aesHeaderY = renderHeader(doc, aesPageWidth, pdfHeader);

      renderTitleAndSubtitle(
        doc,
        MARGINS.left,
        aesHeaderY,
        "Änderung zu 1971-2000",
        label
      );

      const aesContentTop =
        aesHeaderY + HEADER.subtitleYOffset + TABLE_CHART_LAYOUT.topGap;

      renderTableAndChartAt(doc, {
        pageWidth: aesPageWidth,
        contentTop: aesContentTop,
        tableResult: aesTableResult,
        timeOverviewNls,
        chartImageUrl: aesChartImageUrl,
        chartDimensions: aesChartDimensions,
        yAxisLabel: label,
      });
    }

    // Footer
    renderFooter(doc, pageWidth);

    // save
    downloadPdf(doc, label, selectedEntity);
    return doc;
  } catch (e) {
    console.error("Error creating PDF in createFactSheetPdf", e);
    throw e;
  }
}

/**
 * Render the document header: organization text, optional logo.
 * @param {import('jspdf').jsPDF} doc - jsPDF instance
 * @param {number} pageWidth - page width in mm
 * @param {string|null} pdfHeader - optional image data URL for header logo
 * @returns {number} headerY - Y coordinate after the header (mm)
 */
function renderHeader(doc, pageWidth, pdfHeader) {
  const headerLeftX = MARGINS.left;
  const headerText = "NIKLIS Niedersächsisches Klimainformationssystem";
  const headerTextSize = HEADER.textSize;
  const headerTextY = HEADER.textY;

  doc.setFontSize(headerTextSize);
  doc.setTextColor(...PDF_COLORS.magenta);
  doc.setFont(undefined, "bold");
  doc.text(headerText, headerLeftX, headerTextY);

  // reset to black and normal for following text
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "normal");

  // header image top-right
  const imgY = HEADER.imgY;
  const imgW = HEADER.imgW;
  const imgH = (imgW / 541) * 105;
  const imgRightX = pageWidth - imgW - MARGINS.right;
  if (pdfHeader) {
    try {
      doc.addImage(pdfHeader, "PNG", imgRightX, imgY, imgW, imgH);
    } catch (e) {
      // ignore
    }
  }

  // Draw magenta thin bar under the header row (below both text and image)
  const headerBottomY =
    Math.max(headerTextY + HEADER.barHeight, imgY + imgH) + HEADER.barHeight;
  doc.setFillColor(...PDF_COLORS.magenta);
  doc.rect(
    MARGINS.left,
    headerBottomY,
    pageWidth - (MARGINS.left + MARGINS.right),
    HEADER.barHeight,
    "F"
  );

  const headerY = headerBottomY + HEADER.titleYOffset;
  return headerY;
}

/**
 * Render the main title and subtitle.
 * @param {import('jspdf').jsPDF} doc
 * @param {number} x - left X position (mm)
 * @param {number} y - top Y position for the title (mm)
 * @param {string} title
 * @param {string} subtitle
 */
function renderTitleAndSubtitle(doc, x, y, title, subtitle) {
  // Main title
  doc.setFontSize(PDF_FONT.title);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "bold");
  doc.text(title, x, y);

  // Subtitle = selected parameter label (existing `label`)
  doc.setFontSize(PDF_FONT.subtitle);
  doc.text(subtitle || "", x, y + HEADER.subtitleYOffset);

  // reset to normal for following text
  doc.setFont(undefined, "normal");
}

/**
 * Draw the info box containing definition, data source, spatial/time selection and citation.
 * Returns the computed box height so callers can continue layout below it.
 * @param {import('jspdf').jsPDF} doc
 * @param {number} infoBoxX
 * @param {number} infoBoxY
 * @param {number} infoBoxW
 * @param {Object} [opts]
 * @param {string} [opts.infoText]
 * @param {{datengrundlageText?: string}} [opts.nls]
 * @param {string} [opts.entityDisplayText]
 * @param {string} [opts.selectedEntity]
 * @param {string} [opts.Zeitabschnitt]
 * @param {Object|null} [opts.currentChoice]
 * @param {string} [opts.label]
 * @returns {number} infoBoxH
 */
function renderInfoBox(doc, infoBoxX, infoBoxY, infoBoxW, opts = {}) {
  const {
    infoText = "",
    nls = {},
    entityDisplayText = "",
    selectedEntity = "",
    Zeitabschnitt = "",
    currentChoice = null,
    label = "",
  } = opts;

  // Labels column inside box
  const labelX = infoBoxX + BOX.padding;
  const labelNames = [
    "Definition",
    "Datengrundlage",
    "Räumliche Auswahl",
    "Zeitliche Auswahl",
    "Zitiervorschlag",
  ];

  doc.setFontSize(BOX.labelFontSize);
  doc.setFont(undefined, "bold");
  const labelWidths = labelNames.map((l) => doc.getTextWidth(l));
  const maxLabelWidth = Math.max(...labelWidths, 0);
  const labelColWidth = Math.ceil(maxLabelWidth) + BOX.padding; // padding
  const valueX = labelX + labelColWidth;
  doc.setFont(undefined, "normal");

  const valueColumnMaxWidth = infoBoxW - (valueX - infoBoxX);
  const lineHeight = BOX.lineHeight; // approx mm per line for fontSize

  const definitionText = infoText
    ? infoText.replace(/≥/g, ">=").replace(/〞/g, '"')
    : "";
  const definitionLines = definitionText
    ? doc.splitTextToSize(definitionText, valueColumnMaxWidth)
    : [""];

  const dataSourceText = removeAnchorTags(nls.datengrundlageText || "");
  const dataSourceLines = dataSourceText
    ? doc.splitTextToSize(dataSourceText, valueColumnMaxWidth)
    : [""];

  const spatialLabel = `${entityDisplayText}${
    selectedEntity ? "; " + selectedEntity : ""
  }`.trim();
  const spatialSelectionLines = spatialLabel
    ? doc.splitTextToSize(spatialLabel, valueColumnMaxWidth)
    : [""];

  const timespanLabel =
    Zeitabschnitt || (currentChoice && currentChoice.label) || "";
  const timespanLines = timespanLabel
    ? doc.splitTextToSize(timespanLabel, valueColumnMaxWidth)
    : [""];

  // Zitiervorschlag
  const year = new Date().getFullYear();
  const paramLabel = label || "";
  const spatialText = spatialLabel || "";
  const timeText = timespanLabel || "";
  const citationText = `NIKLIS (${year}): Faktenblatt - ${paramLabel} – ${spatialText} im ${timeText}, NIKLIS Niedersächsisches Klimainformationssystem`;
  const citationLines = doc.splitTextToSize(citationText, valueColumnMaxWidth);
  const citationLinesCount = citationLines.length || 1;

  // We'll render the text first and compute the used height, then draw the
  // border so the box tightly wraps its rendered contents.
  const topPadding = BOX.topPadding; // currentY starts at infoBoxY + topPadding
  const rowGap = BOX.rowGap; // spacing after each block
  let currentY = infoBoxY + topPadding;

  doc.setFontSize(BOX.labelFontSize);
  doc.setFont(undefined, "bold");
  doc.text("Definition", labelX, currentY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(BOX.valueFontSize);
  doc.text(definitionLines, valueX, currentY);
  currentY += definitionLines.length * lineHeight + rowGap;

  doc.setFontSize(BOX.labelFontSize);
  doc.setFont(undefined, "bold");
  doc.text("Datengrundlage", labelX, currentY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(BOX.valueFontSize);
  doc.text(dataSourceLines, valueX, currentY);
  currentY += dataSourceLines.length * lineHeight + rowGap;

  doc.setFontSize(BOX.labelFontSize);
  doc.setFont(undefined, "bold");
  doc.text("Räumliche Auswahl", labelX, currentY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(BOX.valueFontSize);
  doc.text(spatialSelectionLines, valueX, currentY);
  currentY += spatialSelectionLines.length * lineHeight + rowGap;

  doc.setFontSize(BOX.labelFontSize);
  doc.setFont(undefined, "bold");
  doc.text("Zeitliche Auswahl", labelX, currentY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(BOX.valueFontSize);
  doc.text(timespanLines, valueX, currentY);
  currentY += timespanLines.length * lineHeight + rowGap;

  // Render Zitiervorschlag inside the info box
  doc.setFontSize(BOX.labelFontSize);
  doc.setFont(undefined, "bold");
  doc.text("Zitiervorschlag", labelX, currentY);
  doc.setFont(undefined, "normal");
  doc.setFontSize(BOX.valueFontSize);
  doc.text(citationLines, valueX, currentY);
  currentY += citationLinesCount * lineHeight;

  // Compute the box height: from top to current position + bottom padding.
  // No need to add rowGap after the last row.
  const usedHeight = currentY - infoBoxY + BOX.padding;
  const infoBoxH = Math.max(BOX.minHeight, usedHeight);

  // blue border (draw after text so the height tightly matches content)
  doc.setDrawColor(...PDF_COLORS.borderBlue);
  doc.setLineWidth(0.75);
  doc.rect(infoBoxX, infoBoxY, infoBoxW, infoBoxH, "S");

  return infoBoxH;
}

/**
 * Draw a vertical y-axis label next to the chart image.
 *
 * The label is rotated 90° counterclockwise and vertically centered along the chart height.
 * This implementation ensures stable positioning regardless of label length by:
 * - Using align: "left" to prevent horizontal shifts based on text length
 * - Manually centering the label vertically by calculating the midpoint
 *
 * @param {import('jspdf').jsPDF} doc - jsPDF instance
 * @param {string} text - The y-axis label text (e.g., "Temperatur", "Klimatische Wasserbilanz")
 * @param {number} imgX - Left X position of the chart image (mm)
 * @param {number} imgH - Height of the chart image (mm)
 * @param {number} contentTop - Top Y position where the chart starts (mm)
 */
function drawVerticalYAxisLabel(doc, text, imgX, imgH, contentTop) {
  if (!text) return;

  doc.saveGraphicsState && doc.saveGraphicsState();
  doc.setFontSize(Y_AXIS_LABEL_FONT_SIZE);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "normal");

  // Calculate horizontal position: offset from chart's left edge
  const labelX = imgX + Y_AXIS_LABEL_OFFSET;

  // Calculate vertical centering:
  // To center the label along the chart height:
  // 1. Find chart vertical center: contentTop + imgH / 2
  // 2. Since text starts at labelY and extends upward by textWidth,
  //    shift down by half the text width to center it: + textWidth / 2
  const textWidth = doc.getTextWidth(String(text));
  const chartVerticalCenter = contentTop + imgH / 2;
  const labelY = chartVerticalCenter + textWidth / 2;

  // Draw rotated text with stable left alignment (prevents horizontal shifts for long labels)
  doc.text(String(text), labelX, labelY, { angle: 90, align: "left" });

  doc.restoreGraphicsState && doc.restoreGraphicsState();
}

/**
 * Render a table-and-chart section at an explicit top Y position.
 * Used for the main page section and the optional change-mode (AES: Änderungen zu 1971-2000) page.
 *
 * @param {import('jspdf').jsPDF} doc
 * @param {Object} opts
 * @param {number} opts.pageWidth - Page width in mm.
 * @param {number} opts.contentTop - Top Y position where the section starts (mm).
 * @param {Array<Object>} opts.tableResult - Rows used to build the time overview table.
 * @param {Object} opts.timeOverviewNls - Localized strings for the time overview table.
 * @param {string|null} opts.chartImageUrl - PNG data URL; when null, a placeholder is drawn.
 * @param {{width?: number, height?: number}} opts.chartDimensions - Chart dimensions used to keep aspect ratio.
 * @param {string} opts.yAxisLabel - Label drawn next to the chart as y-axis title.
 */
function renderTableAndChartAt(doc, opts) {
  const {
    pageWidth,
    contentTop,
    tableResult,
    timeOverviewNls,
    chartImageUrl,
    chartDimensions,
    yAxisLabel,
  } = opts || {};

  // compute left/right column layout locally
  const leftColX = MARGINS.left;
  const leftColW = TABLE_CHART_LAYOUT.leftColWidth;
  const rightColX = leftColX + leftColW + TABLE_CHART_LAYOUT.columnGap;
  const rightColW = pageWidth - rightColX - MARGINS.right;

  // Build shared model and render time overview table
  const timeOverviewModel = buildTimeOverviewTableModel(
    tableResult || [],
    timeOverviewNls || {}
  );

  renderTimeOverviewTableToDoc(
    doc,
    contentTop,
    leftColX,
    leftColW,
    timeOverviewModel
  );

  // const lastTableY = doc.lastAutoTable
  //   ? doc.lastAutoTable.finalY
  //   : contentTop + 40;

  // // Add small caption under left table
  // doc.setFontSize(PDF_FONT.small);
  // doc.text(
  //   "Tabelle mit allen verfügbaren Zeiträumen!",
  //   leftColX,
  //   lastTableY + BOX.infoTitleGap
  // );

  // Add chart image to the right column
  renderChartImageToDoc(doc, {
    chartImageUrl,
    chartDimensions,
    rightColX,
    rightColW,
    contentTop,
    yAxisLabel,
  });
}

/**
 * Render the chart image (with vertical y-axis label) or a placeholder if missing.
 * @param {import('jspdf').jsPDF} doc
 * @param {Object} opts
 * @param {string|null} opts.chartImageUrl
 * @param {{width?: number, height?: number}} opts.chartDimensions
 * @param {number} opts.rightColX
 * @param {number} opts.rightColW
 * @param {number} opts.contentTop
 * @param {string} opts.yAxisLabel
 */
function renderChartImageToDoc(doc, opts) {
  const {
    chartImageUrl,
    chartDimensions = {},
    rightColX,
    rightColW,
    contentTop,
    yAxisLabel = "",
  } = opts || {};

  if (chartImageUrl) {
    const maxW = rightColW;
    const maxH = TABLE_CHART_LAYOUT.chartMaxH;
    let imgW = maxW;
    let imgH =
      (maxW * (chartDimensions?.height || 200)) /
      (chartDimensions?.width || 300);
    if (imgH > maxH) {
      imgH = maxH;
      imgW =
        (imgH * (chartDimensions?.width || 300)) /
        (chartDimensions?.height || 200);
    }
    try {
      // right-align the image inside the right column with a small inner padding
      const imgX = rightColX + Math.max(0, rightColW - imgW - BOX.padding);

      // Draw vertical y-axis label using helper
      const chartY = contentTop + TABLE_CHART_LAYOUT.chartYOffset;
      drawVerticalYAxisLabel(doc, yAxisLabel, imgX, imgH, chartY);

      doc.addImage(
        chartImageUrl,
        "PNG",
        imgX,
        chartY,
        imgW,
        imgH,
        "",
        "NONE",
        0
      );
    } catch (e) {
      // ignore
    }
  } else {
    // placeholder box when chart missing
    doc.setDrawColor(200);
    doc.rect(
      rightColX,
      contentTop,
      rightColW,
      TABLE_CHART_LAYOUT.chartMaxH,
      "S"
    );
    doc.setFontSize(BOX.labelFontSize);
    doc.text(
      "Grafik nicht verfügbar",
      rightColX + BOX.padding,
      contentTop + 10
    );
  }
}

/**
 * Render footer (magenta bar + centered page number) on every page.
 * @param {import('jspdf').jsPDF} doc
 * @param {number} pageWidth
 */
function renderFooter(doc, pageWidth) {
  // Footer: draw on every page
  const pageCount =
    typeof doc.internal.getNumberOfPages === "function"
      ? doc.internal.getNumberOfPages()
      : doc.internal.pages
      ? Object.keys(doc.internal.pages).length
      : 1;
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let p = 1; p <= pageCount; p++) {
    try {
      doc.setPage(p);
    } catch (e) {
      // ignore
    }

    // Draw an inset magenta bar (respect document margins) a few mm above the
    // bottom edge and render the page number centered below the bar.
    const insetX = MARGINS.left;
    const insetW = Math.max(0, pageWidth - MARGINS.left - MARGINS.right);
    const barGapFromBottom = 6; // mm gap from bottom to top of the bar
    const barY = pageHeight - FOOTER.height - barGapFromBottom;

    doc.setFillColor(...PDF_COLORS.magenta);
    doc.rect(insetX, barY, insetW, FOOTER.barHeight, "F");

    // Page number sits below the bar (close to bottom). Keep it centered.
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(FOOTER.fontSize);
    const pageNumText = `- ${p} -`;
    const pageNumY = barY + FOOTER.height + FOOTER.pageNumYOffset;
    doc.text(pageNumText, pageWidth / 2, pageNumY, { align: "center" });
  }
}

/**
 * Download the generated PDF to disk.
 * @param {import('jspdf').jsPDF} doc
 * @param {string} [label]
 * @param {string} [selectedEntity]
 */
function downloadPdf(doc, label = "", selectedEntity = "") {
  const outFilename = `Statistik - ${label}${
    selectedEntity ? " - " + selectedEntity : ""
  }.pdf`;
  try {
    doc.save(outFilename);
  } catch (e) {
    // ignore
  }
}

/**
 * Render the time-overview table into an existing jsPDF document.
 * @param {import('jspdf').jsPDF} doc - jsPDF instance
 * @param {number} startY - Y position to start the table (mm)
 * @param {number} leftColX - left margin X for the table area (mm)
 * @param {number} leftColW - available width for the left table (mm)
 * @param {Object} model - normalized model from buildTimeOverviewTableModel
 * @returns {void}
 */
function renderTimeOverviewTableToDoc(doc, startY, leftColX, leftColW, model) {
  const {
    observations = [],
    projectionRows = [],
    nls: modelNls = {},
  } = model || {};

  const timeNls = modelNls || {};
  const body = [];

  body.push([
    {
      content: timeNls.beobachtungsdaten || "Beobachtungsdaten",
      colSpan: 3,
      styles: {
        halign: "center",
        fillColor: PDF_COLORS.headerBg,
        textColor: 0,
        fontStyle: "bold",
      },
    },
  ]);

  if (observations.length) {
    observations.forEach((item) => {
      body.push([
        { content: item.period || "", styles: { fontSize: PDF_FONT.small } },
        {
          content: item.value || "",
          colSpan: 2,
          styles: { fontSize: PDF_FONT.small },
        },
      ]);
    });
  } else {
    body.push([
      {
        content: "Keine Beobachtungsdaten verfügbar",
        colSpan: 3,
        styles: { halign: "left", fontSize: 8 },
      },
    ]);
  }

  if (projectionRows && projectionRows.length) {
    body.push([
      {
        content: timeNls.projektionsdaten || "Projektionsdaten",
        colSpan: 3,
        styles: {
          halign: "center",
          fillColor: PDF_COLORS.headerBg,
          textColor: 0,
          fontStyle: "bold",
        },
      },
    ]);

    body.push([
      "",
      {
        content: timeNls.rcp26 || "RCP2.6",
        styles: {
          fillColor: PDF_COLORS.rcp26,
          textColor: 0,
          fontStyle: "bold",
          halign: "left",
        },
      },
      {
        content: timeNls.rcp85 || "RCP8.5",
        styles: {
          fillColor: PDF_COLORS.rcp85,
          textColor: 0,
          fontStyle: "bold",
          halign: "left",
        },
      },
    ]);

    projectionRows.forEach((r) => {
      body.push([
        { content: r.period || "", styles: { fontSize: PDF_FONT.small } },
        {
          content: r.rcp26 || "",
          styles: { fillColor: PDF_COLORS.rcp26, fontSize: PDF_FONT.small },
        },
        {
          content: r.rcp85 || "",
          styles: { fillColor: PDF_COLORS.rcp85, fontSize: PDF_FONT.small },
        },
      ]);
    });
  }

  // Compute first column width based on longest period text so it only takes what it needs
  // and let the remaining two columns share the leftover width.
  // Measure widths using the same font size used for table cells
  doc.setFontSize(PDF_FONT.small);
  const periodCandidates = [];
  observations.forEach((o) => periodCandidates.push(o.period || ""));
  projectionRows.forEach((p) => periodCandidates.push(p.period || ""));
  periodCandidates.push(timeNls.attribute || "Zeitraum");
  const measured = periodCandidates.map((t) =>
    Math.ceil(doc.getTextWidth(String(t || "")))
  );
  const maxPeriodWidth = measured.length ? Math.max(...measured) : 20;
  const firstCol = Math.min(
    Math.max(
      LAYOUT.leftColMinWidth,
      Math.ceil(maxPeriodWidth) + LAYOUT.cellPadding
    ),
    leftColW - LAYOUT.cellPadding * 4
  );
  const other = Math.max(LAYOUT.otherColMinWidth, (leftColW - firstCol) / 2);

  autoTable(doc, {
    startY,
    margin: { left: leftColX },
    tableWidth: leftColW,
    body,
    styles: { fontSize: PDF_FONT.normal },
    theme: "grid",
    columnStyles: {
      0: { cellWidth: firstCol },
      1: { cellWidth: other },
      2: { cellWidth: other },
    },
  });
}
